import React, { useState, useEffect } from 'react';
import { sosAPI } from '../api/api';
import { useSocket } from '../context/SocketContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function SOSPage() {
  const { sosAlerts: liveSosAlerts, clearSosAlert } = useSocket();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audioData, setAudioData] = useState({});

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const res = await sosAPI.getAlerts();
      setAlerts(res.data.alerts);
    } catch { toast.error('Failed to load SOS alerts'); }
    finally { setLoading(false); }
  };

  const acknowledge = async (alertId) => {
    try {
      await sosAPI.acknowledge(alertId);
      setAlerts(prev => prev.map(a => a._id === alertId ? { ...a, status: 'acknowledged' } : a));
      clearSosAlert(alertId);
      toast.success('Alert acknowledged');
    } catch { toast.error('Failed to acknowledge'); }
  };

  const loadAudio = async (alertId) => {
    try {
      const res = await sosAPI.getAudio(alertId);
      setAudioData(prev => ({ ...prev, [alertId]: res.data.audioData }));
    } catch { toast.error('No audio available'); }
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'badge-danger';
    if (status === 'acknowledged') return 'badge-warning';
    return 'badge-online';
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>🆘 SOS Alerts</h2>
        <button className="btn btn-ghost btn-sm" onClick={loadAlerts}>🔄 Refresh</button>
      </div>

      {/* Live pending alerts */}
      {liveSosAlerts.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(249,115,22,0.1))',
          border: '1px solid rgba(239,68,68,0.4)', borderRadius: 16,
          padding: 20, marginBottom: 24, animation: 'pulse-border 1s infinite'
        }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#fca5a5', marginBottom: 10 }}>
            🚨 {liveSosAlerts.length} ACTIVE SOS ALERT{liveSosAlerts.length > 1 ? 'S' : ''}
          </div>
          {liveSosAlerts.map((alert, i) => (
            <div key={i} style={{
              background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '12px 16px', marginBottom: 8
            }}>
              <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                {alert.childName} — {alert.message}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                📍 {alert.location?.address || `${alert.location?.latitude?.toFixed(4)}, ${alert.location?.longitude?.toFixed(4)}`}
                {alert.keywords?.length > 0 && <> · 🔑 {alert.keywords.join(', ')}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <h3>No SOS Alerts</h3>
            <p>All is safe! SOS alerts from child devices will appear here.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {alerts.map(alert => (
            <div key={alert._id} className="card" style={{
              borderColor: alert.status === 'active' ? 'rgba(239,68,68,0.4)' : 'var(--border)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                  background: alert.status === 'active' ? 'rgba(239,68,68,0.15)' : 'rgba(107,114,128,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24
                }}>
                  {alert.status === 'active' ? '🚨' : alert.status === 'acknowledged' ? '👁️' : '✅'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      {alert.childId?.name || 'Child'}
                    </span>
                    <span className={`badge ${getStatusColor(alert.status)}`}>
                      {alert.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 6 }}>
                    {alert.message}
                  </div>
                  {alert.location?.latitude && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      📍 {alert.location.address || `${alert.location.latitude.toFixed(6)}, ${alert.location.longitude.toFixed(6)}`}
                    </div>
                  )}
                  {alert.keywords?.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      🔑 Detected: {alert.keywords.join(', ')}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {format(new Date(alert.triggeredAt), 'MMM d, yyyy HH:mm:ss')}
                    {alert.acknowledgedAt && <> · Acknowledged: {format(new Date(alert.acknowledgedAt), 'HH:mm:ss')}</>}
                  </div>

                  {/* Audio playback */}
                  {audioData[alert._id] && (
                    <div style={{ marginTop: 10 }}>
                      <audio controls src={`data:audio/wav;base64,${audioData[alert._id]}`}
                        style={{ width: '100%', height: 36 }} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                  {alert.status === 'active' && (
                    <button className="btn btn-danger btn-sm" onClick={() => acknowledge(alert._id)}>
                      ✓ Acknowledge
                    </button>
                  )}
                  {alert.hasAudio && (
                    <button className="btn btn-ghost btn-sm" onClick={() => loadAudio(alert._id)}>
                      🎤 Play Audio
                    </button>
                  )}
                  {alert.location?.latitude && (
                    <a
                      href={`https://maps.google.com/?q=${alert.location.latitude},${alert.location.longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      className="btn btn-outline btn-sm"
                    >
                      🗺️ Open Map
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
